import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MousePointerClick, Moon, Volume2, VolumeX } from "lucide-react";
import RobotHero3D from "./RobotHero3D";
import { scrollToHash } from "./Navbar";

const LINES = ["TUTTI I SERVIZI", "DI CUI HAI", "BISOGNO, IN", "UN'UNICA AZIENDA"];

// frasi che la mascotte HELPLY mostra nel fumetto; la voce è la registrazione reale del titolare
// "spoken" è la versione per la voce sintetizzata di riserva (se l'audio non parte)
const FUSION_PHRASES = [
  {
    text: "In basso troverai i servizi di HELPLY!",
    spoken: "In basso troverai i servizi di Helpli!",
    audio: "/audio/voce-2.mp3",
    action: "servizi", // scorre alla sezione servizi
  },
  {
    text: "Posso aiutarti a richiedere un preventivo!",
    spoken: "Posso aiutarti a richiedere un preventivo!",
    audio: "/audio/voce-1.mp3",
    action: "preventivo", // apre il modulo preventivo
  },
  {
    text: "Chiamaci subito per altre informazioni!",
    spoken: "Chiamaci subito per altre informazioni!",
    audio: "/audio/voce-3.mp3",
    action: "chiama", // apre il telefono
  },
  {
    text: "Che aspetti? Scrivici su WhatsApp!",
    spoken: "Che aspetti? Scrivici su WhatsApp!",
    audio: "/audio/voce-4.mp3",
    action: "whatsapp", // apre la chat WhatsApp
  },
];

// voci italiane più naturali/delicate, in ordine di preferenza (dipende dal dispositivo)
const PREFERRED_VOICES = ["google italiano", "elsa", "federica", "alice", "sofia", "luca"];

function pickItalianVoice() {
  const voices = window.speechSynthesis.getVoices().filter((v) => v.lang?.toLowerCase().startsWith("it"));
  for (const name of PREFERRED_VOICES) {
    const v = voices.find((voice) => voice.name.toLowerCase().includes(name));
    if (v) return v;
  }
  return voices[0] || null;
}

// riproduce la voce registrata del titolare; se non parte, usa la voce sintetizzata di riserva
// onDone viene chiamato quando la frase finisce di essere pronunciata
let helpyAudio = null;
function playHelpyVoice(phrase, onDone) {
  const done = onDone ? () => setTimeout(onDone, 700) : null;
  try {
    if (helpyAudio) {
      helpyAudio.pause();
      helpyAudio.currentTime = 0;
    }
    helpyAudio = new Audio(phrase.audio);
    helpyAudio.volume = 0.95;
    if (done) helpyAudio.onended = done;
    helpyAudio.play().catch(() => speakHelpy(phrase.spoken, onDone));
  } catch {
    speakHelpy(phrase.spoken, onDone);
  }
}

// vocina delicata e naturale con la Web Speech API (gratuita, usata come riserva)
function speakHelpy(text, onEnd) {
  try {
    if (!("speechSynthesis" in window)) {
      if (onEnd) setTimeout(onEnd, 3500);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "it-IT";
    u.pitch = 1.15; // tono leggermente alto ma naturale
    u.rate = 0.98; // ritmo calmo
    u.volume = 0.9;
    if (onEnd) u.onend = onEnd;
    const voice = pickItalianVoice();
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
  } catch {
    onEnd?.();
  }
}

const STATS = [
  { value: "5", label: "Aree di Servizio" },
  { value: "360°", label: "Copertura Integrata" },
  { value: "H24", label: "Disponibilità" },
];

export default function Hero({ onEarlyAccess }) {
  const [interactions, setInteractions] = useState(0);
  const [sleeping, setSleeping] = useState(false);
  const [fusedPhrase, setFusedPhrase] = useState(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);

  // silenzia HELPLY: ferma subito voce registrata e sintetizzata
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      mutedRef.current = next;
      if (next) {
        if (helpyAudio) helpyAudio.pause();
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      }
      return next;
    });
  };

  // precarica le voci del browser (Chrome le carica in modo asincrono)
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    return () => {
      window.speechSynthesis.cancel();
      if (helpyAudio) helpyAudio.pause();
    };
  }, []);

  const handleFusionChange = (fused) => {
    if (fused) {
      const phrase = FUSION_PHRASES[Math.floor(Math.random() * FUSION_PHRASES.length)];
      setFusedPhrase(phrase.text);
      // la voce parte poco dopo il tintinnio magico; quando finisce, HELPLY esegue l'azione della frase
      // (se silenziato, l'azione parte comunque dopo il tempo di lettura del fumetto)
      setTimeout(() => {
        if (mutedRef.current) {
          setTimeout(() => runPhraseAction(phrase.action), 2500);
        } else {
          playHelpyVoice(phrase, () => runPhraseAction(phrase.action));
        }
      }, 600);
    } else {
      setFusedPhrase(null);
    }
  };

  // dopo la frase, HELPLY passa all'azione: preventivo, chiamata, WhatsApp o scroll ai servizi
  const runPhraseAction = (action) => {
    if (action === "preventivo") {
      onEarlyAccess?.();
    } else if (action === "chiama") {
      window.location.href = "tel:+393755398236";
    } else if (action === "whatsapp") {
      window.open("https://wa.me/393755398236", "_blank", "noopener");
    } else if (action === "servizi") {
      scrollToHash("#funzionalita");
    }
  };

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 30%, rgba(53,101,174,0.14) 0%, rgba(245,130,32,0.10) 40%, rgba(245,248,253,0) 80%)",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 pb-16 pt-16 sm:px-8 lg:grid-cols-[1.25fr_1fr] lg:px-16 lg:pb-24 lg:pt-24">
        <div className="relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-neon"
            data-testid="hero-eyebrow"
          >
            {"// HELPLY SRLS — Servizi integrati per persone, case e aziende"}
          </motion.p>

          <h1 className="font-display text-[1.65rem] font-black uppercase leading-[1.05] tracking-tight sm:text-5xl lg:text-[2.6rem] xl:text-[3.25rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === LINES.length - 1 ? (
                    <span className="text-neon drop-shadow-[0_0_18px_rgba(0,240,255,0.45)]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base"
            data-testid="hero-subtext"
          >
            HELPLY SRLS è il partner unico per pulizie, progettazione del verde,
            gestione del personale e traslochi. Un solo interlocutore, infinite soluzioni.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={onEarlyAccess}
              className="group flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:shadow-[0_12px_40px_rgba(53,101,174,0.5)]"
            >
              Richiedi un Preventivo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-manifesto-button"
              onClick={() => scrollToHash("#manifesto")}
              className="rounded-full border border-[#C9D8EE] px-7 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-[#16345F] transition-all duration-300 hover:border-pulse hover:text-pulse"
            >
              Scopri Chi Siamo
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 grid max-w-md grid-cols-3 gap-4 border-t border-[#D9E4F5] pt-6"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-display text-xl font-bold text-[#16345F] sm:text-2xl">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative h-[360px] sm:h-[500px] lg:h-[640px]"
        >
          <RobotHero3D
            onInteract={() => setInteractions((i) => i + 1)}
            onSleepChange={setSleeping}
            onFusionChange={handleFusionChange}
            muted={muted}
          />
          {/* pulsante silenzia HELPLY */}
          <button
            data-testid="helpy-mute-button"
            onClick={toggleMute}
            aria-label={muted ? "Riattiva la voce di HELPLY" : "Silenzia HELPLY"}
            title={muted ? "Riattiva la voce di HELPLY" : "Silenzia HELPLY"}
            className="absolute left-1 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#3565AE] bg-white text-[#3565AE] shadow-[0_8px_24px_rgba(53,101,174,0.25)] transition-colors duration-300 hover:bg-[#3565AE] hover:text-white sm:left-2"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          {/* fumetto di HELPLY quando la fusione è completata */}
          <AnimatePresence>
            {fusedPhrase && (
              <motion.div
                key={fusedPhrase}
                data-testid="helpy-speech-bubble"
                initial={{ opacity: 0, scale: 0.5, y: 14, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, y: 0, rotate: 2 }}
                exit={{ opacity: 0, scale: 0.6, y: -10, rotate: 0 }}
                transition={{ type: "spring", stiffness: 380, damping: 20 }}
                className="pointer-events-none absolute right-0 top-14 z-10 sm:right-2 sm:top-16"
              >
                <div className="relative rounded-2xl border-2 border-[#3565AE] bg-white px-4 py-2.5 shadow-[0_10px_35px_rgba(22,52,95,0.18)] sm:px-5 sm:py-3">
                  <p className="max-w-[210px] whitespace-normal text-center font-display text-xs font-bold uppercase leading-snug text-[#16345F] sm:max-w-none sm:whitespace-nowrap sm:text-sm">
                    {fusedPhrase}
                  </p>
                  <div className="absolute -bottom-[9px] left-8 h-4 w-4 rotate-45 border-b-2 border-r-2 border-[#3565AE] bg-white" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/* pill invito al click: in alto, blu, ben visibile */}
          <div className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-center">
            <motion.div
              animate={sleeping ? {} : { scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex max-w-[92%] items-center gap-2 rounded-full bg-[#3565AE] px-4 py-2.5 shadow-[0_10px_30px_rgba(53,101,174,0.35)] sm:gap-3 sm:px-5"
            >
              {sleeping ? (
                <Moon className="h-4 w-4 shrink-0 text-[#FFD166]" />
              ) : (
                <MousePointerClick className="h-4 w-4 shrink-0 text-white" />
              )}
              <span className="whitespace-normal text-center font-mono text-[10px] uppercase leading-snug tracking-[0.08em] text-white sm:text-[11px] sm:tracking-[0.12em]">
                {sleeping ? "HELPLY dorme — muovi il mouse" : "Prova a cliccare sul robottino e scoprirai l'effetto"}
              </span>
              <span
                data-testid="hero-interaction-counter"
                className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[#FFD166]"
              >
                {String(interactions).padStart(3, "0")}
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
